import tenantData from '@data';
import PropTypes from 'prop-types';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSubmitBuyPackageLeadMutation } from '../../apis/common';
import {
  buyPackageFormStartedEvent,
  buyPackageFormSubmissionFailedEvent,
  buyPackageFormSubmittedEvent,
} from '../../services/analyticsService';
import { notification } from '../common';
import { JSONFormStyled } from '../common/json-form/json-form';
import { Modal } from '../common/modals/antd-modals';

// Shared lead-capture modal for the Bayut Oman Buy Package flow. Opened from a
// package card; packageId identifies the package the user is inquiring about and is
// sent as the member-route path param. entryPoint/platform are analytics-only.
// Lead capture only — no checkout/payment.
const BuyPackageLeadModal = ({ isVisible, setIsVisible, packageId, packageName, entryPoint, platform }) => {
  const { t } = useTranslation();
  const formRef = useRef();
  // Fire buy_package_form_started only once per open, on the first field interaction.
  const formStartedRef = useRef(false);
  const { user } = useSelector((state) => state.app.loginUser);
  const [submitBuyPackageLead, { isLoading }] = useSubmitBuyPackageLeadMutation();

  const fields = useMemo(() => tenantData.buyPackageLeadFormFields(t), [t]);

  const onCloseModal = () => {
    // Never allow closing mid-submit so we don't drop an in-flight inquiry.
    if (isLoading) return;
    setIsVisible(false);
    formStartedRef.current = false;
    formRef?.current && formRef?.current?.resetForm();
  };

  const handleOk = () => {
    formRef?.current && formRef?.current?.submitForm();
  };

  const handleFormInteraction = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    buyPackageFormStartedEvent(user, entryPoint, platform);
  };

  const handleSubmit = async (values) => {
    const response = await submitBuyPackageLead({ id: packageId, ...values });
    if (response?.error) {
      // Request rejected (validation / auth) — keep the modal open with values retained
      // so the user can retry by submitting again. Never show a false success.
      buyPackageFormSubmissionFailedEvent(user, {
        entryPoint,
        platform,
        errorType: typeof response.error === 'string' ? response.error : 'api_error',
      });
      notification.error(t('Something went wrong while submitting your inquiry. Please try again.'), 7);
    } else {
      buyPackageFormSubmittedEvent(user, { entryPoint, platform, city: values?.city, nationality: values?.nationality });
      // Prefer the BE's own confirmation message; fall back to ours if it's absent.
      const successMessage =
        response?.data?.message || t('Your inquiry has been submitted successfully. Our team will contact you soon.');
      notification.success(successMessage);
      setIsVisible(false);
      formStartedRef.current = false;
      formRef?.current && formRef?.current?.resetForm();
    }
  };

  return (
    <Modal
      type="primary"
      title={packageName ? `${t('Get')} ${packageName}` : t('Buy Package')}
      visible={isVisible}
      okText={t('Submit')}
      okButtonProps={{ type: 'primary' }}
      loading={isLoading}
      onOk={handleOk}
      onCancel={onCloseModal}
      maskClosable={!isLoading}
      keyboard={!isLoading}
      hideCloseIcon={isLoading}
      width={520}
    >
      <div onChangeCapture={handleFormInteraction}>
        <JSONFormStyled
          fields={fields}
          ref={formRef}
          onSubmitForm={handleSubmit}
          noOfContentColumns={1}
        />
      </div>
    </Modal>
  );
};

BuyPackageLeadModal.propTypes = {
  isVisible: PropTypes.bool,
  setIsVisible: PropTypes.func,
  packageId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  packageName: PropTypes.string,
  entryPoint: PropTypes.string,
  platform: PropTypes.string,
};

export default BuyPackageLeadModal;
